import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCoreRowModel, getExpandedRowModel, useReactTable } from "@tanstack/react-table";
import { usePersistedTableState } from "components/table/usePersistedTableState";
import { useCategoriesQuery } from "hooks/useCategories";
import { useBiddingPrograms } from "modules/perf_reports/api/useBiddingPrograms";
import { useGetV4AdvReportByAd } from "modules/perf_reports/api/v4AdvReportByAd";
import { useGetV4AdvReportOptions } from "modules/perf_reports/api/v4AdvReportOptions";
import { useDeferredValue, useMemo } from "react";
import { MdRotateLeft } from "react-icons/md";
import { useAppSelector } from "redux/hooks";
import { NTM_STATUS_OPTIONS, servingStatusOptions } from "./adLifecycleFilters";
import { Table } from "components/table/Table";
import { HStack, VStack } from "../../../styled-system/jsx";
import { Button } from "components/core/basic/Button";
import { RT8FilterWidget } from "views/merchandise/tables/FilterWidget";
import { RT8Pagination } from "components/table/Pagination";
import { css } from "../../../styled-system/css";
import { SearchInput } from "components/fields/base/TextField";
import { RT8ColumnSelector } from "components/core/blocks/MetricSelector";
import { idsToVisibilityState, resetTable } from "components/table/utils";
import { AD_STATES, AD_STATUSES, GOALS } from "components/core/blocks/AdPlan/constants";
import useAdReportColumns from "modules/perf_reports/ad_report/useAdReportColumns";

const AdvertisementSelector = ({
  selectedRowIds = [],
  onSelectionChange,
  defaultSelectedColumns = [
    "state",
    "created_datetime",
    "ad_sales",
    "ad_spend",
    "acos",
    "roas",
    "orders",
    "impressions",
    "clicks",
    "ctr",
    "conversion_rate",
    "cpc",
  ],
  enableMultiRowSelection = true,
  canSetPageSize = true,
  titleColWidth = 500,
  context,
}: {
  selectedRowIds?: string[] | number[];
  onSelectionChange: (selected: number[] | string[]) => void;
  defaultSelectedColumns?: string[];
  enableMultiRowSelection?: boolean;
  canSetPageSize?: boolean;
  titleColWidth?: number;
  context: string;
}) => {
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const columns = useAdReportColumns();

  const EXCLUDED_COLUMNS = ["state", "daily_budget", "target_acos", "target_roas", "max_acos", "min_roas"];

  const filteredColumns = columns
    .filter((c) => !c.id || (c.id && !EXCLUDED_COLUMNS.includes(c.id)))
    .map((c) => {
      if (c.header === "Advertisement") {
        return { ...c, size: titleColWidth };
      }
      return c;
    });

  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id!),
  );

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `adSelectorWidget-${context}`,
    initialState: {
      columnVisibility: defaultHiddenCols,
      pagination: {
        pageSize: 5,
        pageIndex: 0,
      },
      columnOrder: defaultSelectedColumns,
      columnPinning: {
        left: ["ad_name"],
      },
      sorting: [{ id: "ad_sales", desc: true }],
      rowSelection:
        Array.isArray(selectedRowIds) ? Object.fromEntries(selectedRowIds.map((id) => [id.toString(), true])) : {},
    },
  });

  const deferredSearch = useDeferredValue(state.globalFilter);

  const queryClient = useQueryClient();
  const byAdOptionsGetter = useGetV4AdvReportByAd();
  const advByAdReportQuery = useQuery(
    byAdOptionsGetter({
      pagination: state.pagination,
      sorting: state.sorting,
      filters: state.columnFilters,
      search: deferredSearch,
    }),
  );

  void queryClient.prefetchQuery({
    ...byAdOptionsGetter({
      pagination: { pageIndex: state.pagination.pageIndex + 1, pageSize: state.pagination.pageSize },
      sorting: state.sorting,
      filters: state.columnFilters,
      search: deferredSearch,
    }),
    staleTime: 10000,
  });

  const advReportOptionsQuery = useQuery(useGetV4AdvReportOptions());
  const categoriesQuery = useCategoriesQuery();
  const biddingProgramOptionsQuery = useBiddingPrograms();

  const tagOptions: { value: string; label: string }[] = useMemo((): { value: string; label: string }[] => {
    const tags = advReportOptionsQuery.data?.data.tags ?? [];

    return [
      { value: "all", label: "All Tags" },
      ...tags.map((tag: string): { value: string; label: string } => ({
        value: tag,
        label: tag,
      })),
    ];
  }, [advReportOptionsQuery.data?.data.tags]);

  const biddingProgramOptions: { value: number | string; label: string }[] = useMemo((): {
    value: number | string;
    label: string;
  }[] => {
    const biddingPrograms =
      biddingProgramOptionsQuery.data?.results.map((program) => ({ value: program.id, label: program.name })) ?? [];

    return [{ value: "all", label: "All Rules" }, ...biddingPrograms];
  }, [biddingProgramOptionsQuery.data?.results]);

  const categoryOptions = useMemo(
    () => [
      { value: "all", label: "All Categories" },
      ...(categoriesQuery.data?.map((category) => {
        return {
          value: category.id.toString(),
          label: category.leaf_category_name,
        };
      }) ?? []),
    ],
    [categoriesQuery.data],
  );
  const table = useReactTable({
    data: advByAdReportQuery.data?.data ?? [],
    columns: filteredColumns,
    defaultColumn: {
      filterFn: () => true,
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    getRowId: (row) => row.ad_id.toString(),
    enableRowSelection: (row) => {
      return !(row.original.ad_goal === GOALS.IMPORTED_ADS.value && channel === "walmart");
    },
    enableMultiRowSelection: enableMultiRowSelection,
    rowCount: advByAdReportQuery.data?.count || 0,
    initialState: initialState,
    state: {
      ...state,
      rowSelection:
        Array.isArray(selectedRowIds) ? Object.fromEntries(selectedRowIds.map((id) => [id.toString(), true])) : {},
    },
    ...setters,
    manualPagination: true,
    onRowSelectionChange: (updater) => {
      const rowSelectionState = typeof updater === "function" ? updater(table.getState().rowSelection) : updater;
      const updatedIds = Object.keys(rowSelectionState);
      onSelectionChange(updatedIds);
    },
    meta: {
      showSkeleton: advByAdReportQuery.isFetching && (!advByAdReportQuery.data || advByAdReportQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        tags: tagOptions,
        category_id: categoryOptions,
        state: [{ value: "all", label: "All States" }, AD_STATES.ACTIVE, AD_STATES.PAUSE, AD_STATES.ENDED],
        ad_status: [{ value: "all", label: "All Statuses" }, ...Object.values(AD_STATUSES)],
        serving_status: servingStatusOptions,
        day_parting_method: [
          {
            value: "all",
            label: "All",
          },
          {
            value: true,
            label: "With Dayparting",
          },
          {
            value: false,
            label: "Without Dayparting",
          },
        ],
        ntm_enrollment_status: [
          {
            label: "All Statuses",
            value: "all",
          },
          ...NTM_STATUS_OPTIONS,
        ],
        has_active_scheduled_actions: [
          {
            value: true,
            label: "With Scheduled Actions",
          },
          {
            value: false,
            label: "Without Scheduled Actions",
          },
        ],
        program_id: biddingProgramOptions,
        ad_source: [
          {
            label: "All Sources",
            value: "all",
          },
          {
            value: "Trellis",
            label: "Trellis",
          },
          {
            value: "Imported",
            label: "Imported",
          },
        ],
      },
    },
  });

  return (
    <div>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack>
          <HStack>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={"Search"}
              isExpandable
              className={css({ flex: "0 1 350px" })}
              value={table.getState().globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination table={table} canSetPageSize={canSetPageSize} />
      </HStack>
      <Table table={table} />
    </div>
  );
};

export default AdvertisementSelector;
